import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ApiError } from '../../lib/api';
import { updateAccount } from '../accounts';
import { useCurrentUser } from '../auth';
import type {
  AccountCoverage,
  CoverageTier,
  ReportingCoverage,
  ReportingPullMode,
} from '../../types/reporting';
import { getReportingCoverage, triggerBackfill } from './api';
import { pullModeLabel } from './backfillView';
import {
  EMPTY_COVERAGE_FILTERS,
  anyCoverageRunning,
  coverageGapDays,
  demographicsPullStatus,
  demographicsPullStatusLabel,
  filterCoverageRows,
  formatConnectedAge,
  formatCoverageRange,
  gapSeverity,
  needsFullBackfill,
  sortCoverage,
  structuralPullStatus,
  structuralPullStatusLabel,
  summarizeLocations,
  tierGapNote,
  tierGapTooltip,
  type BackfillFilter,
  type ConnectedFilter,
  type CoverageFilters,
  type CoverageSort,
  type CoverageSortField,
  type CoverageTierKey,
  type DemographicsPullStatus,
  type GapFilter,
  type GapSeverity,
  type HasFilter,
  type StructuralPullStatus,
} from './coverageView';

// Coverage page (/reporting/coverage, admin-only). A quiet, scannable ops CONTROL
// PANEL: per account, what data each tier has (campaign / ad-structural /
// demographics) PLUS the levers to fill it — the daily-sync toggle + the four
// tiered pull triggers (Phase 2). Filters ("who's had what") surface the
// onboarding worklist: accounts off daily sync, accounts with no demographics yet.
// Polls faster while a pull runs so the running annotation + re-enabled triggers
// stay live. Derived from BigQuery + Firestore, always accurate.

const COVERAGE_KEY = ['reporting', 'coverage'] as const;

const TIERS: { key: CoverageTierKey; label: string }[] = [
  { key: 'campaign', label: 'Campaign' },
  { key: 'adStructural', label: 'Ad-structural' },
  { key: 'demographics', label: 'Demographics' },
];

const SORT_PROPS: { prop: 'from' | 'to' | 'days'; label: string }[] = [
  { prop: 'from', label: 'start' },
  { prop: 'to', label: 'end' },
  { prop: 'days', label: 'days' },
];

const PULL_MODES: ReportingPullMode[] = [
  'structural-recent',
  'structural-full',
  'demographics-recent',
  'demographics-full',
];

// A per-tier pull-depth chip: None (muted) / recent-only (amber) / deep-backfill
// done (green). Absent for the campaign tier (no pull-tier choice — it's the
// baseline). Renders even for a null tier, where it reads "None" — the signal
// that this tier still needs its first pull.
function PullChip({ label, status }: { label: string; status: 'none' | 'recent' | 'full' }) {
  const tone =
    status === 'none'
      ? 'bg-slate-100 text-slate-500'
      : status === 'recent'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-kyck-50 text-kyck-700';
  return (
    <span
      className={`inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${tone}`}
    >
      {label}
    </span>
  );
}

// One tier cell: the compact range + a quiet, muted no-activity note (threshold-
// gated, informational not a warning; exact count on hover). An optional pull-
// status chip renders beneath (ad-structural / demographics; the campaign tier
// passes none).
function TierCell({ tier, chip }: { tier: CoverageTier | null; chip?: React.ReactNode }) {
  const note = tier ? tierGapNote(tier) : null;
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-1.5 tabular-nums text-slate-700">
        {tier ? <span>{formatCoverageRange(tier)}</span> : <span className="text-slate-300">—</span>}
        {note && (
          <span
            className="text-[11px] font-normal normal-case tracking-normal text-slate-400"
            title={tierGapTooltip(tier) ?? undefined}
          >
            {note}
          </span>
        )}
      </span>
      {chip}
    </div>
  );
}

function SyncToggle({
  on,
  busy,
  onToggle,
}: {
  on: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Recurring daily sync"
      disabled={busy}
      onClick={onToggle}
      className={[
        'inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
        on ? 'bg-kyck-600' : 'bg-slate-300',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-4' : 'translate-x-0.5',
        ].join(' ')}
      />
    </button>
  );
}

function FacetSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs text-slate-500">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-kyck-400 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const HAS_OPTIONS: { value: HasFilter; label: string }[] = [
  { value: 'all', label: 'Any' },
  { value: 'yes', label: 'Has data' },
  { value: 'no', label: 'None' },
];

const STRUCTURAL_PULL_OPTIONS: { value: 'all' | StructuralPullStatus; label: string }[] = [
  { value: 'all', label: 'Any' },
  { value: 'none', label: 'None' },
  { value: 'recent', label: '90-day' },
  { value: 'full', label: 'Full (25mo)' },
];

const DEMOGRAPHICS_PULL_OPTIONS: { value: 'all' | DemographicsPullStatus; label: string }[] = [
  { value: 'all', label: 'Any' },
  { value: 'none', label: 'None' },
  { value: 'recent', label: 'Recent' },
  { value: 'full', label: 'Full' },
];

// #2 — the rolled-up onboarding-worklist facet (survives partial ingest).
const BACKFILL_OPTIONS: { value: BackfillFilter; label: string }[] = [
  { value: 'all', label: 'Any' },
  { value: 'needs-full', label: 'Needs full backfill' },
  { value: 'complete', label: 'Fully backfilled' },
];

// #3 — connection recency.
const CONNECTED_OPTIONS: { value: ConnectedFilter; label: string }[] = [
  { value: 'all', label: 'Any time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

// #4 — worst-gap magnitude.
const GAP_OPTIONS: { value: GapFilter; label: string }[] = [
  { value: 'all', label: 'Any' },
  { value: 'gt30', label: '> 30 days' },
  { value: 'gt90', label: '> 90 days' },
];

// #2 — the tier-based "needs a full 25-month backfill" badge (amber). Shown on a
// row whenever structural OR demographics is below FULL — it stays visible through
// a partial ingest, which is the whole point (a 90-day sliver is NOT done).
function NeedsBackfillBadge() {
  return (
    <span
      className="inline-flex w-fit items-center rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700"
      title="Reporting-eligible but not fully backfilled — structural and/or demographics is below the full 25-month depth (a partial/recent ingest still counts as needing the full pull)."
    >
      Needs backfill
    </span>
  );
}

// #4 — the worst-gap chip. Muted for a small gap (usually genuine no-spend), amber
// for a sizeable one, red only when a big gap sits on a NOT-fully-backfilled tier
// (the reliable "likely a real hole" signal). Never for a trivial gap.
const GAP_CHIP_TONE: Record<Exclude<GapSeverity, 'none'>, string> = {
  notice: 'bg-slate-100 text-slate-500',
  warn: 'bg-amber-50 text-amber-700',
  alert: 'bg-red-50 text-red-700',
};

function GapChip({ row }: { row: AccountCoverage }) {
  const gap = coverageGapDays(row);
  const sev = gapSeverity(row);
  if (sev === 'none') return <span className="text-slate-300">—</span>;
  const title =
    sev === 'alert'
      ? `${gap} calendar days have no data on a not-fully-backfilled account — likely a missing pull rather than no-spend. Run a full backfill, then re-check.`
      : sev === 'warn'
        ? `${gap} calendar days have no data — worth a look. Could be a missing pull or a genuine no-spend stretch (we can't tell interior holes from trailing no-spend from the day-count alone).`
        : `${gap} calendar days have no data — usually days the account had no ads delivering.`;
  return (
    <span
      className={`inline-flex w-fit items-center rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${GAP_CHIP_TONE[sev]}`}
      title={title}
    >
      {gap}d
    </span>
  );
}

export function CoveragePage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<CoverageFilters>(EMPTY_COVERAGE_FILTERS);
  const [sort, setSort] = useState<CoverageSort>({ field: 'account', dir: 'asc' });

  const coverage = useQuery({
    queryKey: COVERAGE_KEY,
    queryFn: () => getReportingCoverage(), // whole roster (enriched: sync flag + in-flight status)
    enabled: me.role === 'admin',
    // Poll faster while any pull runs so the running annotation clears live; else gently.
    refetchInterval: (q) => (anyCoverageRunning(q.state.data?.accounts ?? []) ? 10_000 : 60_000),
  });

  // Daily-sync toggle (Build A's reportingSyncEnabled mutation, audited server-side).
  // Operates at the AD-ACCOUNT level: flips reportingSyncEnabled across EVERY cluster
  // member (a shared ad account is synced as a unit). Optimistic: flip the cached row
  // immediately, roll back on error, reconcile on settle. `rowId` keys the optimistic
  // cache write (the ad-account row); `accountIds` are the members actually written.
  const toggleSync = useMutation({
    mutationFn: (v: { rowId: string; accountIds: string[]; next: boolean }) =>
      Promise.all(v.accountIds.map((id) => updateAccount(id, { reportingSyncEnabled: v.next }))),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: COVERAGE_KEY });
      const prev = qc.getQueryData<ReportingCoverage>(COVERAGE_KEY);
      if (prev) {
        qc.setQueryData<ReportingCoverage>(COVERAGE_KEY, {
          accounts: prev.accounts.map((a) =>
            a.accountId === v.rowId ? { ...a, reportingSyncEnabled: v.next } : a,
          ),
        });
      }
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(COVERAGE_KEY, ctx.prev);
      toast.error(e instanceof ApiError ? e.message : "Couldn't update daily sync.");
    },
    onSuccess: (_a, v) => toast.success(v.next ? 'Daily sync on.' : 'Daily sync off.'),
    onSettled: () => void qc.invalidateQueries({ queryKey: COVERAGE_KEY }),
  });

  // Fire a tiered pull (Phase 1) — scoped to the row's REPRESENTATIVE account (the
  // §3b canonical puller), so one trigger backfills the whole ad account (cluster)
  // once. On success, optimistically mark the row running so its triggers disable
  // immediately; the poll then tracks the real status. `rowId` keys the cache write.
  const trigger = useMutation({
    mutationFn: (v: {
      rowId: string;
      representativeAccountId: string;
      accountName: string;
      mode: ReportingPullMode;
    }) => triggerBackfill(v.representativeAccountId, { pullMode: v.mode }),
    onSuccess: (_r, v) => {
      const prev = qc.getQueryData<ReportingCoverage>(COVERAGE_KEY);
      if (prev) {
        qc.setQueryData<ReportingCoverage>(COVERAGE_KEY, {
          accounts: prev.accounts.map((a) =>
            a.accountId === v.rowId ? { ...a, activeBackfill: { pullMode: v.mode } } : a,
          ),
        });
      }
      void qc.invalidateQueries({ queryKey: COVERAGE_KEY });
      toast.success('Pull started', {
        description: `${pullModeLabel(v.mode)} for ${v.accountName}.`,
        action: { label: 'View', onClick: () => window.location.assign('/reporting/backfills') },
      });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't start the pull."),
  });

  const rows = useMemo<AccountCoverage[]>(() => {
    const all = coverage.data?.accounts ?? [];
    return sortCoverage(filterCoverageRows(all, filters), sort);
  }, [coverage.data, filters, sort]);

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view reporting coverage.</p>;
  }

  const toggleSort = (field: CoverageSortField) =>
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'account' ? 'asc' : 'desc' },
    );

  const arrow = (field: CoverageSortField): string =>
    sort.field === field ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const total = coverage.data?.accounts.length ?? 0;
  const patch = (p: Partial<CoverageFilters>) => setFilters((f) => ({ ...f, ...p }));

  return (
    <div>
      <PageHeader
        title="Coverage"
        description="What data each account has, per tier — and the controls to fill it: flip daily sync on, then kick the tiered structural + demographics pulls. Build the onboarding worklist by filtering: recently connected accounts, those still needing a full 25-month backfill (any tier below FULL — survives partial ingests), and data-gap alerts. Derived from BigQuery; pulls run in the background."
        backHref="/reporting"
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={filters.text}
          onChange={(e) => patch({ text: e.target.value })}
          placeholder="Filter by account or brand…"
          className="w-64 rounded-md border border-slate-200 px-3 py-1.5 text-sm focus:border-kyck-400 focus:outline-none focus:ring-1 focus:ring-kyck-400"
        />
        <FacetSelect
          label="Daily sync"
          value={filters.dailySync}
          onChange={(v) => patch({ dailySync: v as CoverageFilters['dailySync'] })}
          options={[
            { value: 'all', label: 'Any' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
        />
        <FacetSelect
          label="Ad-structural"
          value={filters.adStructural}
          onChange={(v) => patch({ adStructural: v as HasFilter })}
          options={HAS_OPTIONS}
        />
        <FacetSelect
          label="Demographics"
          value={filters.demographics}
          onChange={(v) => patch({ demographics: v as HasFilter })}
          options={HAS_OPTIONS}
        />
        <FacetSelect
          label="Structural pull"
          value={filters.adStructuralPull}
          onChange={(v) => patch({ adStructuralPull: v as CoverageFilters['adStructuralPull'] })}
          options={STRUCTURAL_PULL_OPTIONS}
        />
        <FacetSelect
          label="Demographics pull"
          value={filters.demographicsPull}
          onChange={(v) => patch({ demographicsPull: v as CoverageFilters['demographicsPull'] })}
          options={DEMOGRAPHICS_PULL_OPTIONS}
        />
        <FacetSelect
          label="Backfill"
          value={filters.backfill}
          onChange={(v) => patch({ backfill: v as BackfillFilter })}
          options={BACKFILL_OPTIONS}
        />
        <FacetSelect
          label="Connected"
          value={filters.connected}
          onChange={(v) => patch({ connected: v as ConnectedFilter })}
          options={CONNECTED_OPTIONS}
        />
        <FacetSelect
          label="Gap"
          value={filters.gap}
          onChange={(v) => patch({ gap: v as GapFilter })}
          options={GAP_OPTIONS}
        />
        {coverage.data && (
          <span className="text-xs text-slate-500">
            {rows.length === total ? `${total} accounts` : `${rows.length} of ${total} accounts`}
          </span>
        )}
      </div>

      {coverage.isLoading ? (
        <CenteredSpinner />
      ) : coverage.error ? (
        <p className="text-sm text-red-600">Couldn't load coverage.</p>
      ) : total === 0 ? (
        <p className="text-sm text-slate-600">
          No coverage yet. The first pull (or backfill) will populate this.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-4 py-2">
                  <button
                    type="button"
                    className="font-medium uppercase tracking-wide hover:text-slate-900"
                    onClick={() => toggleSort('account')}
                  >
                    Account{arrow('account')}
                  </button>
                </th>
                <th className="px-4 py-2">
                  <button
                    type="button"
                    className="font-medium uppercase tracking-wide hover:text-slate-900"
                    onClick={() => toggleSort('connected')}
                    title="When this account's Meta connection was first made (onboarding finalize). The freshly-connected worklist."
                  >
                    Connected{arrow('connected')}
                  </button>
                </th>
                {TIERS.map((t) => (
                  <th key={t.key} className="px-4 py-2">
                    <div className="font-medium uppercase tracking-wide text-slate-600">{t.label}</div>
                    <div className="mt-0.5 flex gap-2 text-[11px] normal-case tracking-normal">
                      {SORT_PROPS.map((sp) => {
                        const field = `${t.key}.${sp.prop}` as CoverageSortField;
                        const active = sort.field === field;
                        return (
                          <button
                            key={sp.prop}
                            type="button"
                            className={active ? 'font-semibold text-kyck-700' : 'text-slate-400 hover:text-slate-700'}
                            onClick={() => toggleSort(field)}
                          >
                            {sp.label}
                            {arrow(field)}
                          </button>
                        );
                      })}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-2">
                  <button
                    type="button"
                    className="font-medium uppercase tracking-wide hover:text-slate-900"
                    onClick={() => toggleSort('gap')}
                    title="Worst no-data gap across tiers (span − days with data). Big gaps on not-fully-backfilled accounts are likely a missing pull; small ones are usually genuine no-spend."
                  >
                    Gap{arrow('gap')}
                  </button>
                </th>
                <th className="px-4 py-2 font-medium uppercase tracking-wide text-slate-600">Daily sync</th>
                <th className="px-4 py-2 font-medium uppercase tracking-wide text-slate-600">Pull</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const syncBusy =
                  toggleSync.isPending && toggleSync.variables?.rowId === row.accountId;
                const running = row.activeBackfill !== null;
                const triggerBusy =
                  running || (trigger.isPending && trigger.variables?.rowId === row.accountId);
                return (
                  <tr key={row.accountId} className="hover:bg-slate-50">
                    <td
                      className="max-w-[16rem] px-4 py-2 align-top"
                      title={row.adAccountId || undefined}
                    >
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="truncate font-medium text-slate-900">
                          {row.accountName ?? row.adAccountId}
                        </span>
                        {row.isShared && (
                          <span className="inline-flex items-center rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-700">
                            shared ({row.locationNames.length})
                          </span>
                        )}
                        {needsFullBackfill(row) && <NeedsBackfillBadge />}
                      </div>
                      {row.isShared ? (
                        <div
                          className="mt-0.5 truncate text-xs text-slate-500"
                          title={row.locationNames.join(', ')}
                        >
                          {summarizeLocations(row.locationNames)}
                        </div>
                      ) : (
                        row.brandName && (
                          <div className="truncate text-xs text-slate-500">{row.brandName}</div>
                        )
                      )}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <span
                        className="whitespace-nowrap tabular-nums text-slate-700"
                        title={row.metaConnectedAt ?? 'Connection time unknown (legacy / linked outside onboarding)'}
                      >
                        {formatConnectedAge(row.metaConnectedAt)}
                      </span>
                    </td>
                    <td className="px-4 py-2 align-top">
                      <TierCell tier={row.campaign} />
                    </td>
                    <td className="px-4 py-2 align-top">
                      <TierCell
                        tier={row.adStructural}
                        chip={
                          <PullChip
                            status={structuralPullStatus(row.adStructural)}
                            label={structuralPullStatusLabel(structuralPullStatus(row.adStructural))}
                          />
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top">
                      <TierCell
                        tier={row.demographics}
                        chip={
                          <PullChip
                            status={demographicsPullStatus(row.demographics)}
                            label={demographicsPullStatusLabel(
                              demographicsPullStatus(row.demographics),
                            )}
                          />
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top">
                      <GapChip row={row} />
                    </td>
                    <td className="px-4 py-2 align-top">
                      <SyncToggle
                        on={row.reportingSyncEnabled}
                        busy={syncBusy}
                        onToggle={() =>
                          toggleSync.mutate({
                            rowId: row.accountId,
                            accountIds: row.locationIds,
                            next: !row.reportingSyncEnabled,
                          })
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top">
                      {running ? (
                        <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-amber-700">
                          <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                          {pullModeLabel(row.activeBackfill!.pullMode)} running…
                        </span>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            disabled={triggerBusy}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                          >
                            Pull data
                            <ChevronDown className="h-3 w-3" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Trigger a tiered pull</DropdownMenuLabel>
                            {PULL_MODES.map((mode) => (
                              <DropdownMenuItem
                                key={mode}
                                onSelect={() =>
                                  trigger.mutate({
                                    rowId: row.accountId,
                                    representativeAccountId: row.representativeAccountId,
                                    accountName: row.accountName ?? row.adAccountId,
                                    mode,
                                  })
                                }
                              >
                                {pullModeLabel(mode)}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
